import { useCallback, useEffect, useRef, useState } from 'react';

// Same cadence WaitingRoomScreen.jsx already polls /rooms/mine at — reused
// here for chat's own poll so both stay in sync with how "live" a waiting
// room feels overall.
const POLL_INTERVAL_MS = 3000;

function tempId() {
    return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Data/merge logic for the room chat feature (waiting room + in-game),
 * shared by both surfaces via ChatPanel.jsx — see the chat feature plan.
 *
 * `wsClient` is `null` for the waiting room (no WebSocket exists for a room
 * that hasn't started a game yet — see WaitingRoomScreen.jsx's own comment
 * on this) and drives poll mode; passing the real GameWsClient instance
 * switches to WS-push mode (one-time REST backfill, then live CHAT_MESSAGE
 * events, no polling at all).
 *
 * Messages are deduped by their server `id` (a UUID) — an optimistic own-send
 * gets a temporary local id and a `pending: true` flag, replaced by the real
 * row once the POST resolves; if that same message later also arrives via
 * WS (it will, since the server broadcasts to every socket including the
 * sender's own), merging by id makes that a no-op.
 */
export default function useRoomChat({ roomId, authFetch, wsClient, userId, enabled = true }) {
    const [messages, setMessages] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState(null);

    const lastSeqRef = useRef(0);
    const unmountedRef = useRef(false);

    const mergeById = useCallback((incoming) => {
        if (!incoming.length) return;
        setMessages((prev) => {
            const byId = new Map(prev.map((m) => [m.id, m]));
            for (const m of incoming) byId.set(m.id, m);
            return Array.from(byId.values()).sort((a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity));
        });
        for (const m of incoming) {
            if (typeof m.seq === 'number') lastSeqRef.current = Math.max(lastSeqRef.current, m.seq);
        }
    }, []);

    useEffect(() => {
        // Spectators aren't members of the room, so there's no chat for them to read.
        if (!enabled) return undefined;
        unmountedRef.current = false;
        let intervalId;
        let unsubscribe;

        async function backfill() {
            try {
                const data = await authFetch(`/rooms/${roomId}/chat`);
                if (!unmountedRef.current) mergeById(data.messages);
            } catch {
                // A failed backfill just leaves the panel empty for now — the
                // next poll tick (waiting room) or a later WS message
                // (in-game) will still bring the conversation current.
            }
        }

        backfill();

        if (wsClient) {
            unsubscribe = wsClient.on('CHAT_MESSAGE', (msg) => {
                if (!unmountedRef.current) mergeById([msg.payload]);
            });
        } else {
            intervalId = setInterval(async () => {
                try {
                    const data = await authFetch(`/rooms/${roomId}/chat?after=${lastSeqRef.current}`);
                    if (!unmountedRef.current) mergeById(data.messages);
                } catch {
                    // Transient network hiccup — the next tick tries again.
                }
            }, POLL_INTERVAL_MS);
        }

        return () => {
            unmountedRef.current = true;
            if (intervalId) clearInterval(intervalId);
            if (unsubscribe) unsubscribe();
        };
    }, [roomId, authFetch, wsClient, mergeById, enabled]);

    const sendMessage = useCallback(
        async (kind, body) => {
            const id = tempId();
            const optimistic = {
                id,
                seq: null,
                senderUserId: userId,
                senderSeat: null,
                senderName: null,
                kind,
                body,
                createdAt: new Date().toISOString(),
                pending: true,
            };
            setMessages((prev) => [...prev, optimistic]);
            setIsSending(true);
            setSendError(null);
            try {
                const created = await authFetch(`/rooms/${roomId}/chat`, { method: 'POST', body: { kind, body } });
                setMessages((prev) => prev.filter((m) => m.id !== id));
                mergeById([created]);
            } catch (err) {
                setMessages((prev) => prev.filter((m) => m.id !== id));
                setSendError(err?.code ?? 'UNKNOWN_ERROR');
            } finally {
                if (!unmountedRef.current) setIsSending(false);
            }
        },
        [roomId, authFetch, mergeById, userId]
    );

    return { messages, isSending, sendError, sendMessage };
}
